import { z } from "zod";
import { organizationRoleSchema, type OrganizationRole } from "./roles";
import type { OrganizationType } from "./types";

/** Catálogo de permissões granulares — recurso × ação, não por procedure individual.
 *  Preço/serviços e transferência de propriedade ficam de fora de propósito: são
 *  trancadas em OWNER e nunca aparecem como toggle (ver visiblePrice / ownerProcedure). */
export const PERMISSION_KEYS = [
  "patients.view",
  "patients.manage",
  "patients.export",
  "patients.configure",
  "treatments.manage",
  "anamnese.view",
  "anamnese.manage",
  "agenda.view",
  "agenda.manage",
  "agenda.viewAll",
  "files.manage",
  "prosthesis.view",
  "prosthesis.manage",
  "prosthesis.viewAll",
  "partners.manage",
  "production.view",
  "production.manage",
  "production.viewAll",
  "production.export",
  "labCatalog.manage",
  "stock.manage",
  "team.manage",
  "settings.manage",
  "prices.view",
  "prices.manage",
  "finance.view",
  "finance.manage",
] as const;

export type PermissionKey = (typeof PERMISSION_KEYS)[number];

/** Pra chaves com par view/manage, ter "manage" concede "view" automaticamente. */
export const PERMISSION_IMPLIED_BY: Partial<Record<PermissionKey, PermissionKey>> = {
  "patients.view": "patients.manage",
  "anamnese.view": "anamnese.manage",
  "agenda.view": "agenda.manage",
  "prosthesis.view": "prosthesis.manage",
  "production.view": "production.manage",
  "prices.view": "prices.manage",
  "finance.view": "finance.manage",
};

/** "radiology" entra aqui quando a área existir — o agrupamento/filtro da UI já está pronto. */
export type PermissionScope = "clinic" | "lab" | "shared";

type PermissionGroup = {
  resource: string;
  label: string;
  /** Área a que o recurso pertence — a matriz agrupa por isso e esconde a área que a organização
   *  não tem (só visual: defaults e overrides continuam resolvendo no servidor). "shared" vale
   *  pros dois lados: Parceiros é a clínica conectando laboratórios E o laboratório gerenciando
   *  dentistas; Arquivos e Equipe idem. */
  scope: PermissionScope;
  keys: { key: PermissionKey; label: string }[];
};

/** Agrupamento + rótulos PT-BR pra renderizar a matriz em Configurações → Permissões. */
export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    resource: "patients",
    label: "Pacientes",
    scope: "clinic",
    keys: [
      { key: "patients.view", label: "Só visualizar" },
      { key: "patients.manage", label: "Criar e editar" },
      // Levar a base inteira pra fora (CPF, contato) não é o mesmo que ver a ficha: nenhum outro
      // nível concede, e o padrão fica só com Proprietário e Administrador.
      { key: "patients.export", label: "Exportar a lista de pacientes (planilha)" },
      // Muda o cadastro, a anamnese e a ficha de toda a clínica: fica com quem administra.
      { key: "patients.configure", label: "Personalizar cadastro, anamnese e ficha" },
      { key: "treatments.manage", label: "Montar planos de tratamento e orçamentos" },
      // Dado de saúde: separado do "ver pacientes" — a recepção envia o link sem ler as respostas.
      { key: "anamnese.view", label: "Ver anamneses (dados de saúde)" },
      { key: "anamnese.manage", label: "Preencher e revisar anamneses" },
    ],
  },
  {
    resource: "agenda",
    label: "Agenda",
    scope: "clinic",
    keys: [
      { key: "agenda.view", label: "Só visualizar" },
      { key: "agenda.manage", label: "Criar e editar consultas" },
      { key: "agenda.viewAll", label: "Ver a agenda de todos (não só as suas consultas)" },
    ],
  },
  {
    resource: "prosthesis",
    label: "Prótese",
    scope: "clinic",
    keys: [
      { key: "prosthesis.view", label: "Só visualizar" },
      { key: "prosthesis.manage", label: "Criar e gerenciar pedidos" },
      { key: "prosthesis.viewAll", label: "Ver todos os pedidos (não só os que você criou)" },
    ],
  },
  {
    resource: "production",
    label: "Produção",
    scope: "lab",
    keys: [
      { key: "production.view", label: "Só visualizar" },
      { key: "production.manage", label: "Aceitar/recusar pedidos e avançar produção" },
      { key: "production.viewAll", label: "Ver toda a produção (não só os trabalhos delegados a você)" },
      { key: "production.export", label: "Exportar planilhas de pedidos e produção (têm nome de paciente)" },
    ],
  },
  {
    resource: "labCatalog",
    label: "Catálogo",
    scope: "lab",
    keys: [{ key: "labCatalog.manage", label: "Editar materiais, cores e categorias de serviço" }],
  },
  {
    resource: "stock",
    label: "Estoque",
    scope: "lab",
    keys: [{ key: "stock.manage", label: "Ver saldos, lançar compras e ajustes de estoque" }],
  },
  {
    resource: "files",
    label: "Arquivos",
    scope: "shared",
    keys: [{ key: "files.manage", label: "Gerenciar arquivos" }],
  },
  {
    resource: "partners",
    label: "Parceiros e laboratórios",
    scope: "shared",
    keys: [{ key: "partners.manage", label: "Gerenciar contatos e conexões" }],
  },
  {
    resource: "team",
    label: "Equipe",
    scope: "shared",
    keys: [{ key: "team.manage", label: "Convidar e gerenciar membros" }],
  },
  {
    resource: "settings",
    label: "Configurações da organização",
    scope: "shared",
    keys: [{ key: "settings.manage", label: "Editar perfil, especialidades e agenda" }],
  },
  {
    resource: "prices",
    label: "Preços",
    scope: "shared",
    keys: [
      { key: "prices.view", label: "Ver preços (pedidos, catálogo e tabelas)" },
      { key: "prices.manage", label: "Editar preços (pede o PIN)" },
    ],
  },
  {
    resource: "finance",
    label: "Financeiro",
    scope: "lab",
    keys: [
      { key: "finance.view", label: "Ver o financeiro" },
      { key: "finance.manage", label: "Lançar, marcar pago e estornar" },
    ],
  },
];

/** Matriz default por papel — só usada quando não há RolePermissionOverride pra aquela
 *  combinação (organização, papel, chave). OWNER nunca é consultado aqui (sempre tem tudo). */
/** Escopo por papel (clínica): Dentista e Assistente só veem consultas e pedidos SEUS (sem os
 *  viewAll); Recepção e Administrador veem de todos. VIEWER mantém viewAll da agenda — é papel de
 *  observador só-leitura, e sem isso não sobraria nada pra ele ver. */
export const DEFAULT_ROLE_PERMISSIONS: Record<Exclude<OrganizationRole, "OWNER">, PermissionKey[]> = {
  // Nunca consultado (Sócio tem acesso total, como o proprietário) — está aqui pro Record fechar.
  PARTNER: [...PERMISSION_KEYS],
  VIEWER: ["patients.view", "agenda.view", "agenda.viewAll", "prosthesis.view"],
  RECEPTIONIST: [
    "patients.manage",
    "agenda.manage",
    "agenda.viewAll",
    "prosthesis.view",
    "prosthesis.viewAll",
  ],
  ASSISTANT: ["patients.manage", "anamnese.view", "agenda.manage", "files.manage", "prosthesis.manage"],
  DENTIST: ["patients.manage", "treatments.manage", "anamnese.manage", "agenda.manage", "files.manage", "prosthesis.manage", "partners.manage"],
  ADMIN: [
    "patients.manage",
    "patients.export",
    "patients.configure",
    "treatments.manage",
    "anamnese.manage",
    "agenda.manage",
    "agenda.viewAll",
    "files.manage",
    "prosthesis.manage",
    "prosthesis.viewAll",
    "partners.manage",
    "production.manage",
    "production.viewAll",
    "production.export",
    "labCatalog.manage",
    "stock.manage",
    "team.manage",
    "settings.manage",
  ],
};

/** Delta aplicado quando a organização é (também) laboratório — soma aos defaults acima. Corrige
 *  o gap de "colaborador de laboratório não acessa Produção" (issue #10) — sem isso, só ADMIN/OWNER
 *  conseguiam operar produção num laboratório recém-criado. Deliberadamente NÃO inclui
 *  `labCatalog.manage` — a issue #10 é explícita que catálogo fica de fora do colaborador comum,
 *  só produção e arquivos. */
const LAB_ADDITIONAL_PERMISSIONS: Partial<Record<Exclude<OrganizationRole, "OWNER">, PermissionKey[]>> = {
  VIEWER: ["production.view"],
  RECEPTIONIST: ["production.manage", "files.manage"],
  ASSISTANT: ["production.manage"],
  DENTIST: ["production.manage"],
};

/** Removido quando a organização é SÓ laboratório (nunca também clínica) — "Cadastros" (contatos
 *  Partner/ProsthesisLab) fica restrito a quem gerencia (OWNER/ADMIN), não pro colaborador comum
 *  (issue #10). Organização híbrida mantém — quem também é clínica precisa gerenciar parceiros de
 *  verdade, então não faz sentido tirar aqui. */
const LAB_ONLY_REMOVED_PERMISSIONS: Partial<Record<Exclude<OrganizationRole, "OWNER">, PermissionKey[]>> = {
  RECEPTIONIST: ["partners.manage"],
  ASSISTANT: ["partners.manage"],
  DENTIST: ["partners.manage"],
};

/** Default efetivo por papel, considerando o(s) tipo(s) da organização — `DEFAULT_ROLE_PERMISSIONS`
 *  sozinha é pensada pra clínica; isso ajusta pra laboratório (issue #10) sem duplicar a tabela
 *  inteira. Organização sem nenhum tipo configurado (legado) cai no default puro de clínica. */
export function getDefaultRolePermissions(
  role: Exclude<OrganizationRole, "OWNER">,
  orgTypes: OrganizationType[],
): PermissionKey[] {
  const result = new Set(DEFAULT_ROLE_PERMISSIONS[role]);
  const isLab = orgTypes.includes("DENTAL_LAB");
  const isOffice = orgTypes.includes("DENTAL_OFFICE");
  if (isLab) {
    for (const key of LAB_ADDITIONAL_PERMISSIONS[role] ?? []) result.add(key);
    if (!isOffice) {
      for (const key of LAB_ONLY_REMOVED_PERMISSIONS[role] ?? []) result.delete(key);
    }
  }
  return [...result];
}

/** OWNER nunca é um alvo válido — tem acesso total hardcoded, nunca lido/gravado no banco. */
export const setRolePermissionSchema = z.object({
  role: organizationRoleSchema.exclude(["OWNER", "PARTNER"]),
  permissionKey: z.enum(PERMISSION_KEYS),
  granted: z.boolean(),
});
export type SetRolePermissionInput = z.infer<typeof setRolePermissionSchema>;

/** Cria um papel customizado (clone de um papel-base com nome/permissões próprios) — só OWNER.
 *  `basedOnRole` é imutável depois de criado (ver CustomRole.basedOnRole no schema.prisma). */
export const permissionChangeSchema = z.object({
  permissionKey: z.enum(PERMISSION_KEYS),
  granted: z.boolean(),
});
export type PermissionChange = z.infer<typeof permissionChangeSchema>;

const listaDeMudancas = z.array(permissionChangeSchema).min(1).max(PERMISSION_KEYS.length);

export const customRoleCreateSchema = z.object({
  label: z.string().trim().min(2).max(60),
  basedOnRole: organizationRoleSchema.exclude(["OWNER", "PARTNER"]),
  /** Permissões do acesso novo. Chave ausente herda o papel-base — sem nada, é o clone puro. */
  permissions: listaDeMudancas.optional(),
  /** Atribui o acesso recém-criado a este membro, na mesma transação. */
  assignToMembershipId: z.string().min(1).optional(),
});
export type CustomRoleCreateInput = z.infer<typeof customRoleCreateSchema>;

export const customRoleRenameSchema = z.object({
  id: z.string().min(1),
  label: z.string().trim().min(2).max(60),
});
export type CustomRoleRenameInput = z.infer<typeof customRoleRenameSchema>;

/** Ajusta uma chave do snapshot de permissões de um papel customizado já criado — as 12 linhas já
 *  existem desde a criação, isso é sempre um update, nunca um upsert esparso. */
export const customRoleSetPermissionSchema = z.object({
  customRoleId: z.string().min(1),
  permissionKey: z.enum(PERMISSION_KEYS),
  granted: z.boolean(),
});
export type CustomRoleSetPermissionInput = z.infer<typeof customRoleSetPermissionSchema>;

/** Várias chaves de uma vez — salvar o diálogo do colaborador é um gesto só, não N gravações. */
export const customRoleSetPermissionsSchema = z.object({
  customRoleId: z.string().min(1),
  changes: listaDeMudancas,
});
export type CustomRoleSetPermissionsInput = z.infer<typeof customRoleSetPermissionsSchema>;

export const setRolePermissionsSchema = z.object({
  role: organizationRoleSchema.exclude(["OWNER", "PARTNER"]),
  changes: listaDeMudancas,
});
export type SetRolePermissionsInput = z.infer<typeof setRolePermissionsSchema>;

/** Um acesso: papel padrão ou personalizado — exatamente um dos dois. */
export const permissoesDoAcessoSchema = z
  .object({
    role: organizationRoleSchema.exclude(["OWNER", "PARTNER"]).optional(),
    customRoleId: z.string().min(1).optional(),
  })
  .refine((v) => !!v.role !== !!v.customRoleId, { message: "Informe o papel ou o acesso personalizado." });
export type PermissoesDoAcessoInput = z.infer<typeof permissoesDoAcessoSchema>;

export const customRoleDeleteSchema = z.object({
  id: z.string().min(1),
});
export type CustomRoleDeleteInput = z.infer<typeof customRoleDeleteSchema>;
